import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FiX, FiTrendingUp, FiCircle, FiDollarSign } from 'react-icons/fi';
import { BsFire, BsCurrencyBitcoin, BsHexagon, BsDiamond, BsTriangle } from 'react-icons/bs';
import {
    SiTether,
    SiPolkadot,
    SiBinance,
    SiChainlink,
} from 'react-icons/si';
import Button from '../../../components/Button/Button';
import { _getUserStakingPortfolio, _getPublicStakingProducts } from '../redux/actions';
import { setPortfolioData, setStakingPools, setStakingLoading, cleanupExpiredPendingSubscriptions } from '../redux/slices/stakingSlice';
import Styles from './stakingPortfolioPage.module.css';

const StakingPortfolioPage = () => {
    console.log('StakingPortfolioPage component is rendering');

    const dispatch = useDispatch();
    const { portfolioData, portfolioLoading, userData, stakingPools, stakingLoading, pendingSubscriptions } = useSelector(state => ({
        portfolioData: state.staking.portfolio.data,
        portfolioLoading: state.staking.portfolio.loading,
        userData: state.userData.value,
        stakingPools: state.staking.pools,
        stakingLoading: state.staking.loading,
        pendingSubscriptions: state.staking.pendingSubscriptions
    }));

    // console.log('StakingPortfolioPage state:', { portfolioData, portfolioLoading, userData, stakingPools, stakingLoading, pendingSubscriptions });

    const [showStakeDetails, setShowStakeDetails] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(null);
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState('7d');
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);


    // Helper functions
    const getDefaultColor = (asset) => {
        const colorMap = {
            'BTC': '#f7931a',
            'ETH': '#627eea',
            'SOL': '#9945ff',
            'TRX': '#ff060a',
            'FLR': '#e84142',
            'ADA': '#0033ad',
            'DOT': '#e6007a',
            'USDT': '#26a17b',
            'BNB': '#f3ba2f',
            'MATIC': '#8247e5',
            'LINK': '#375bd2',
            'AVAX': '#e84142'
        };
        return colorMap[asset] || '#8b9aab';
    };

    // Get full coin name from asset symbol
    const getCoinName = (asset) => {
        const coinNames = {
            'BTC': 'Bitcoin',
            'ETH': 'Ethereum',
            'SOL': 'Solana',
            'TRX': 'TRON',
            'FLR': 'Flare',
            'ADA': 'Cardano',
            'DOT': 'Polkadot',
            'USDT': 'Tether USD',
            'USDC': 'USD Coin',
            'BNB': 'Binance Coin',
            'MATIC': 'Polygon',
            'LINK': 'Chainlink',
            'AVAX': 'Avalanche'
        };
        return coinNames[asset] || asset;
    };

    // Default icon and color mapping for fallback (same as StakingMainPage)
    const getDefaultIcon = (currencyId) => {
        const Icon = (() => {
            switch (currencyId?.toLowerCase()) {
                case 'btc': return BsCurrencyBitcoin;
                case 'eth': return BsDiamond;
                case 'sol': return BsHexagon;
                case 'trx': return BsTriangle;
                case 'flr': return BsFire;
                case 'ada': return BsHexagon;
                case 'dot': return SiPolkadot;
                case 'usdt': return SiTether;
                case 'bnb': return SiBinance;
                case 'matic': return BsTriangle;
                case 'link': return SiChainlink;
                case 'avax': return BsTriangle;
                case 'atom': return FiCircle;
                case 'usdc': return FiDollarSign;
                case 'algo': return FiCircle;
                case 'xtz': return BsHexagon;
                case 'near': return FiCircle;
                default: return FiCircle;
            }
        })();
        return <Icon size={24} />;
    };

    const getRiskLevel = (apr) => {
        if (apr >= 20) return 'High';
        if (apr >= 10) return 'Medium';
        return 'Low';
    };

    // Transform API data to display format
    const transformStakingData = (apiData) => {
        if (!apiData || !Array.isArray(apiData) || apiData.length === 0) return [];

        return apiData.map((asset, index) => {
            const periods = asset.period_summary_list || [];
            if (periods.length === 0) return null;

            const bestPeriod = periods.reduce((best, current) => {
                return parseFloat(current.apr || 0) > parseFloat(best.apr || 0) ? current : best;
            }, periods[0] || {});

            return {
                id: index + 1,
                coin: getCoinName(asset.asset),
                symbol: asset.asset,
                color: getDefaultColor(asset.asset),
                amount: 0,
                apy: parseFloat(bestPeriod.apr || 0) * 100,
                startDate: new Date().toISOString().split('T')[0],
                endDate: bestPeriod.duration > 0 ?
                    new Date(Date.now() + bestPeriod.duration * 24 * 60 * 60 * 1000).toISOString().split('T')[0] :
                    null,
                earned: 0,
                status: 'Available',
                type: bestPeriod.type || (bestPeriod.duration === 0 ? 'flexible' : 'fixed'),
                duration: bestPeriod.type === 'flexible' ? 'Flexible' : `${bestPeriod.duration} Days`,
                rewards: bestPeriod.reward || (bestPeriod.type === 'flexible' ? 'Daily' : 'At Maturity'),
                risk: bestPeriod.risk || getRiskLevel(parseFloat(bestPeriod.apr || 0)),
                plan: `${asset.asset} ${bestPeriod.duration === 0 ? 'Flexible' : bestPeriod.duration + '-Day'} Staking`,
                minAmount: parseFloat(bestPeriod.min_amount || 0),
                maxAmount: parseFloat(bestPeriod.max_amount || 0),
                periodId: bestPeriod.period_id
            };
        }).filter(Boolean); // Remove null entries
    };

    // Transform active staking data from API
    const transformActiveStakingData = (apiData) => {
        if (!apiData || !Array.isArray(apiData)) return [];

        return apiData.map((stake) => {
            const currency = stake.currency?.toUpperCase() || 'UNKNOWN';
            const endDate = stake.end_date ? new Date(stake.end_date) :
                (stake.start_date && stake.coin_duration ?
                    new Date(new Date(stake.start_date).getTime() + stake.coin_duration * 24 * 60 * 60 * 1000) :
                    null);

            return {
                id: stake.id,
                coin: getCoinName(currency),
                symbol: currency,
                color: getDefaultColor(currency),
                amount: stake.amount || 0,
                apy: stake.apr || 0, // API provides APR, we'll use it as APY for display
                startDate: stake.start_date ? new Date(stake.start_date).toISOString().split('T')[0] : null,
                endDate: endDate ? endDate.toISOString().split('T')[0] : null,
                earned: stake.earned || 0,
                status: 'Active',
                type: stake.coin_duration === 0 ? 'Flexible' : 'Locked',
                duration: stake.coin_duration === 0 ? 'Flexible' : `${stake.coin_duration} Days`,
                rewards: 'Daily', // Based on API structure, rewards appear to be daily
                risk: getRiskLevel(stake.apr || 0),
                plan: `${currency} ${stake.coin_duration === 0 ? 'Flexible' : stake.coin_duration + '-Day'} Staking`,
                iconUrl: stake.icon_url,
                // Additional fields for better display
                rawStartDate: stake.start_date,
                rawEndDate: stake.end_date,
                coinDuration: stake.coin_duration
            };
        });
    };

    // Transform pending subscriptions to match the staking format
    const transformPendingSubscriptions = (pendingSubs) => {
        if (!pendingSubs || !Array.isArray(pendingSubs)) {
            return [];
        }

        return pendingSubs.map((pending) => {
            // Ensure we have a valid symbol/asset
            const asset = pending.asset || pending.currency || 'UNKNOWN';
            const coinName = getCoinName(asset);

            return {
                id: pending.id,
                coin: coinName,
                currency: asset,
                asset,
                symbol: asset, // Ensure symbol is set
                amount: pending.amount,
                apy: pending.apr, // Use APR as APY for display
                apr: pending.apr,
                earned: 0, // No earnings yet for pending
                status: 'Pending',
                type: pending.type,
                duration: pending.duration,
                rewards: pending.rewards,
                risk: pending.risk,
                plan: pending.plan,
                iconUrl: pending.iconUrl,
                color: getDefaultColor(asset), // Add color for consistency
                // Additional fields for better display
                rawStartDate: pending.createdAt,
                rawEndDate: null, // No end date for pending
                coinDuration: pending.durationDays,
                isPending: true // Flag to identify pending subscriptions
            };
        });
    };

    // Use real API data for available staking options - only when loaded
    const availableStakings = (stakingPools && !stakingLoading) ? transformStakingData(stakingPools) : [];
    const activeStakings = (portfolioData?.active_staking && !portfolioLoading) ? transformActiveStakingData(portfolioData.active_staking) : [];
    const pendingStakings = transformPendingSubscriptions(pendingSubscriptions);

    // Combine active and pending stakings - only show when data is actually loaded
    const myStakings = [...activeStakings, ...pendingStakings];

    // Use only API data for portfolio stats - only when loaded
    const portfolioStats = (portfolioData && !portfolioLoading) ? portfolioData : {
        total_value_usd: 0,
        total_earned_dollar: 0,
        total_earned_percentage_month: 0,
        average_apy: 0
    };

    // Fetch staking pools from API
    const fetchStakingPools = () => {
        dispatch(setStakingLoading(true));

        _getPublicStakingProducts(
            { page: 1, limit: 50 },
            (response) => {
                const data = Array.isArray(response) ? response : response.data || [];
                dispatch(setStakingPools({
                    data,
                    total: data.length,
                    page: 1
                }));
                dispatch(setStakingLoading(false));
                setInitialDataLoaded(true);
            },
            (error) => {
                console.error('Error fetching staking pools:', error);
                dispatch(setStakingLoading(false));
                setInitialDataLoaded(true);
            }
        );
    };

    useEffect(() => {
        // Fetch staking pools
        fetchStakingPools();

        // Fetch user portfolio if logged in
        if (userData?.email) {
            _getUserStakingPortfolio(
                (response) => {
                    dispatch(setPortfolioData(response));
                    setInitialDataLoaded(true);
                },
                (error) => {
                    console.error('Error fetching user portfolio:', error);
                    setInitialDataLoaded(true);
                }
            );
        } else {
            // If user is not logged in, still set initial data as loaded
            setInitialDataLoaded(true);
        }

        // Clean up expired pending subscriptions on page load
        dispatch(cleanupExpiredPendingSubscriptions());
    }, [dispatch, userData?.email]);

    const calculateDaysLeft = (stake) => {
        // If it's flexible staking, return 'Flexible'
        if (stake.type === 'Flexible' || stake.coinDuration === 0) {
            return 'Flexible';
        }

        // If we have an explicit end date from API
        if (stake.rawEndDate) {
            const today = new Date();
            const end = new Date(stake.rawEndDate);
            const diffTime = end - today;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays > 0 ? `${diffDays} days` : 'Completed';
        }

        // Calculate end date from start date + duration
        if (stake.rawStartDate && stake.coinDuration) {
            const startDate = new Date(stake.rawStartDate);
            const endDate = new Date(startDate.getTime() + stake.coinDuration * 24 * 60 * 60 * 1000);
            const today = new Date();
            const diffTime = endDate - today;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays > 0 ? `${diffDays} days` : 'Completed';
        }

        return 'N/A';
    };

    const formatNumber = (num, decimals = 2) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(num);
    };

    const getRiskColor = (risk) => {
        switch (risk?.toLowerCase()) {
            case 'low': return '#10b981';
            case 'medium': return '#f59e0b';
            case 'high': return '#ef4444';
            default: return '#8b9aab';
        }
    };

    if (portfolioLoading || stakingLoading || !initialDataLoaded) {
        return (
            <div className={Styles.portfolioContainer} style={{
                backgroundColor: 'var(--background-color)',
                color: 'var(--text-color)',
                minHeight: '100vh',
                padding: '24px'
            }}>
                {/* Header Shimmer */}
                <div style={{
                    backgroundColor: 'var(--secondary-color)',
                    borderRadius: '16px',
                    padding: '32px',
                    marginBottom: '24px',
                    border: '1px solid rgba(139, 154, 171, 0.1)'
                }}>
                    <div className={Styles.shimmer} style={{
                        height: '32px',
                        borderRadius: '8px',
                        marginBottom: '16px'
                    }}></div>
                    <div className={Styles.shimmer} style={{
                        height: '16px',
                        borderRadius: '8px',
                        width: '60%'
                    }}></div>

                    {/* Stats Grid Shimmer */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '24px',
                        marginTop: '32px'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '16px'
                        }}>
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} style={{
                                    backgroundColor: '#000000',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    border: '1px solid rgba(139, 154, 171, 0.1)'
                                }}>
                                    <div className={Styles.shimmer} style={{
                                        height: '12px',
                                        borderRadius: '6px',
                                        marginBottom: '8px'
                                    }}></div>
                                    <div className={Styles.shimmer} style={{
                                        height: '20px',
                                        borderRadius: '6px',
                                        width: '70%'
                                    }}></div>
                                </div>
                            ))}
                        </div>
                        <div style={{
                            backgroundColor: '#000000',
                            borderRadius: '12px',
                            padding: '20px',
                            border: '1px solid rgba(139, 154, 171, 0.1)'
                        }}>
                            <div className={Styles.shimmer} style={{
                                height: '20px',
                                borderRadius: '6px',
                                marginBottom: '16px'
                            }}></div>
                            <div className={Styles.shimmer} style={{
                                height: '30px',
                                borderRadius: '6px',
                                marginBottom: '16px'
                            }}></div>
                            <div className={Styles.shimmer} style={{
                                height: '100px',
                                borderRadius: '6px'
                            }}></div>
                        </div>
                    </div>
                </div>

                {/* Stakes Section Shimmer */}
                <div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '24px'
                    }}>
                        <div className={Styles.shimmer} style={{
                            height: '24px',
                            borderRadius: '6px',
                            width: '200px'
                        }}></div>
                        <div className={Styles.shimmer} style={{
                            height: '36px',
                            borderRadius: '6px',
                            width: '120px'
                        }}></div>
                    </div>

                    {/* Stakes List Shimmer */}
                    <div style={{
                        display: 'grid',
                        gap: '16px'
                    }}>
                        {[1, 2, 3].map((i) => (
                            <div key={i} style={{
                                backgroundColor: '#000000',
                                borderRadius: '12px',
                                padding: '20px',
                                border: '1px solid rgba(139, 154, 171, 0.1)'
                            }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(6, 1fr)',
                                    gap: '16px',
                                    alignItems: 'center'
                                }}>
                                    {/* Coin Info */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}>
                                        <div className={Styles.shimmer} style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%'
                                        }}></div>
                                        <div>
                                            <div className={Styles.shimmer} style={{
                                                height: '16px',
                                                borderRadius: '4px',
                                                width: '80px',
                                                marginBottom: '4px'
                                            }}></div>
                                            <div className={Styles.shimmer} style={{
                                                height: '12px',
                                                borderRadius: '4px',
                                                width: '40px'
                                            }}></div>
                                        </div>
                                    </div>

                                    {/* Other columns */}
                                    {[1, 2, 3, 4, 5].map((col) => (
                                        <div key={col}>
                                            <div className={Styles.shimmer} style={{
                                                height: '12px',
                                                borderRadius: '4px',
                                                marginBottom: '4px'
                                            }}></div>
                                            <div className={Styles.shimmer} style={{
                                                height: '16px',
                                                borderRadius: '4px',
                                                width: '60%'
                                            }}></div>
                                        </div>
                                    ))}

                                    {/* Action Button */}
                                    <div className={Styles.shimmer} style={{
                                        height: '36px',
                                        borderRadius: '6px'
                                    }}></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        );
    }

    // Fallback to ensure something renders
    if (!Styles || !Styles.portfolioContainer) {
        return (
            <div style={{ padding: '20px', color: 'white', background: 'black' }}>
                <h1>Staking Portfolio</h1>
                <p>Loading staking portfolio...</p>
            </div>
        );
    }

    return (
        <div className={Styles.portfolioContainer} style={{
            backgroundColor: 'var(--background-color)',
            color: 'var(--text-color)',
            minHeight: '100vh',
            padding: '24px'
        }}>
            {/* Header with Portfolio Overview */}
            <div className={Styles.headerSection} style={{
                backgroundColor: 'var(--secondary-color)',
                borderRadius: '16px',
                padding: '32px',
                marginBottom: '24px',
                border: '1px solid rgba(139, 154, 171, 0.1)'
            }}>
                <h1 className={Styles.headerTitle} style={{ color: 'var(--text-color)', fontSize: '32px', marginBottom: '16px' }}>
                    My Staking Portfolio
                </h1>
                <p className={Styles.headerDescription} style={{ color: 'var(--text-color)', fontSize: '16px', opacity: 0.7 }}>
                    Track your active staking positions and earnings
                </p>

                {/* Two Column Layout */}
                <div className={Styles.overviewGrid}>
                    {/* Left Column - Portfolio Stats */}
                    <div className={Styles.statsColumn}>
                        <div className={Styles.statsGrid}>
                            <div className={Styles.statCard} style={{ backgroundColor: '#000000' }}>
                                <div className={Styles.statLabel} style={{ color: 'var(--text-color)' }}>
                                    Total Staked Value
                                </div>
                                <p className={Styles.statValue} style={{ color: 'var(--text-color)' }}>
                                    ${formatNumber(portfolioStats.total_value_usd)}
                                </p>
                            </div>

                            <div className={Styles.statCard} style={{ backgroundColor: '#000000' }}>
                                <div className={Styles.statLabel} style={{ color: 'var(--text-color)' }}>
                                    Total Earned
                                </div>
                                <p className={Styles.statValue} style={{ color: 'var(--text-color)' }}>
                                    ${formatNumber(portfolioStats.total_earned_dollar)}
                                </p>
                            </div>

                            <div className={Styles.statCard} style={{ backgroundColor: '#000000' }}>
                                <div className={Styles.statLabel} style={{ color: 'var(--text-color)' }}>
                                    Average APY
                                </div>
                                <p className={Styles.statValue} style={{ color: 'var(--text-color)' }}>
                                    {formatNumber(portfolioStats.average_apy, 1)}%
                                </p>
                            </div>

                            <div className={Styles.statCard} style={{ backgroundColor: '#000000' }}>
                                <div className={Styles.statLabel} style={{ color: 'var(--text-color)' }}>
                                    Active Stakes
                                </div>
                                <p className={Styles.statValue} style={{ color: 'var(--text-color)' }}>
                                    {activeStakings.length}
                                    {pendingStakings.length > 0 && (
                                        <span style={{
                                            fontSize: '12px',
                                            color: '#f59e0b',
                                            marginLeft: '4px'
                                        }}>
                                            (+{pendingStakings.length} pending)
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Performance Chart */}
                    <div className={Styles.performanceColumn} style={{ backgroundColor: '#000000' }}>
                        <h3 className={Styles.performanceTitle} style={{ color: 'var(--text-color)' }}>
                            Performance
                        </h3>

                        {/* Time Period Buttons */}
                        <div className={Styles.periodButtons}>
                            {['7d', '30d', '90d', '1y'].map((period, index) => (
                                <button
                                    key={period}
                                    className={`${Styles.periodButton} ${selectedPeriod === period ? Styles.periodButtonActive : ''
                                        }`}
                                    onClick={() => setSelectedPeriod(period)}
                                >
                                    {period}
                                </button>
                            ))}
                        </div>

                        {/* Performance Value and Change */}
                        <div className={Styles.performanceValue}>
                            <div className={Styles.performanceAmount} style={{ color: 'var(--text-color)' }}>
                                ${formatNumber(portfolioStats.total_earned_dollar)}
                            </div>
                            <div className={Styles.performanceChange}>
                                <FiTrendingUp className={Styles.trendIcon} />
                                <span className={Styles.changeText} style={{ color: 'var(--primary-color)' }}>
                                    +{formatNumber(portfolioStats.total_earned_percentage_month, 1)}%
                                </span>
                            </div>
                        </div>

                        {/* Chart Placeholder */}
                        <div className={Styles.chartPlaceholder}>
                            Performance Chart
                        </div>
                    </div>
                </div>
            </div>

            {/* Active Stakes List */}
            <div className={Styles.stakesSection}>
                <div className={Styles.stakesHeader}>
                    <h2 className={Styles.stakesTitle} style={{ color: 'var(--text-color)' }}>
                        My Stakes
                    </h2>

                    <Button
                        text="Explore More"
                        onClick={() => {
                            window.location.href = '/staking';
                        }}
                        className={Styles.exploreButton}
                        fitContent={true}
                    />
                </div>

                <div className={Styles.stakesList}>
                    {myStakings.length > 0 ? (
                        // Show active stakes if user has any
                        myStakings.map((stake) => (
                            <div key={stake.id} className={Styles.stakeCard}>
                                <div className={Styles.stakeGrid}>
                                    {/* Coin Info */}
                                    <div className={Styles.coinInfo}>
                                        <div
                                            className={Styles.coinIcon}
                                            style={stake.iconUrl ? {
                                                backgroundImage: `url(${stake.iconUrl})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                backgroundRepeat: 'no-repeat',
                                                backgroundColor: 'transparent'
                                            } : { backgroundColor: stake.color }}
                                        >
                                            {!stake.iconUrl && getDefaultIcon(stake.symbol)}
                                        </div>
                                        <div className={Styles.coinDetails}>
                                            <div className={Styles.coinName}>
                                                {stake.coin || 'Unknown'}
                                                {stake.isPending && (
                                                    <span className={Styles.pendingBadge} style={{
                                                        marginLeft: '8px',
                                                        padding: '2px 8px',
                                                        backgroundColor: '#f59e0b',
                                                        color: '#000',
                                                        borderRadius: '12px',
                                                        fontSize: '10px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        PENDING
                                                    </span>
                                                )}
                                            </div>
                                            <div className={Styles.coinSymbol}>
                                                {stake.symbol || '?'}
                                            </div>
                                            <div className={Styles.coinDuration}>
                                                {stake.duration || 'N/A'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Amount */}
                                    <div className={Styles.stakeInfo}>
                                        <div className={Styles.stakeLabel}>Amount</div>
                                        <div className={Styles.stakeValue}>
                                            {formatNumber(stake.amount || 0, stake.symbol === 'BTC' || stake.symbol === 'ETH' ? 4 : 2)} {stake.symbol || '?'}
                                        </div>
                                    </div>

                                    {/* Earned */}
                                    <div className={Styles.stakeInfo}>
                                        <div className={Styles.stakeLabel}>Earned</div>
                                        <div className={Styles.stakeEarned}>
                                            {formatNumber(stake.earned || 0, stake.symbol === 'BTC' || stake.symbol === 'ETH' ? 6 : 2)} {stake.symbol || '?'}
                                        </div>
                                    </div>

                                    {/* End Date */}
                                    <div className={Styles.stakeInfo}>
                                        <div className={Styles.stakeLabel}>End Date</div>
                                        <div className={Styles.stakeValue}>
                                            {stake.endDate ? new Date(stake.endDate).toLocaleDateString() : 'Flexible'}
                                        </div>
                                    </div>

                                    {/* APR */}
                                    <div className={Styles.stakeInfo}>
                                        <div className={Styles.stakeLabel}>APR</div>
                                        <div className={Styles.stakeAPY}>
                                            {stake.apy || 0}%
                                        </div>
                                    </div>

                                    {/* Days Left */}
                                    <div className={Styles.stakeInfo}>
                                        <div className={Styles.stakeLabel}>Days Left</div>
                                        <div
                                            className={Styles.stakeDays}
                                            style={{
                                                color: calculateDaysLeft(stake).includes('days') && parseInt(calculateDaysLeft(stake)) <= 7 ? '#f59e0b' : '#ffffff'
                                            }}
                                        >
                                            {calculateDaysLeft(stake)}
                                        </div>
                                    </div>

                                    {/* Action Button */}
                                    <div className={Styles.stakeActions}>
                                        <button
                                            onClick={() => setShowStakeDetails(stake)}
                                            className={Styles.viewButton}
                                        >
                                            View
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : null}
                </div>

                {myStakings.length === 0 && !portfolioLoading && initialDataLoaded && (
                    <div className={Styles.emptyState}>
                        <p className={Styles.emptyTitle}>No active stakes</p>
                        <p className={Styles.emptyDescription}>You don't have any active staking positions yet</p>
                    </div>
                )}
            </div>

            {/* Stake Details Modal */}
            {showStakeDetails && (
                <div className={Styles.modalOverlay}>
                    <div className={Styles.modalContent}>
                        {/* Modal Header */}
                        <div className={Styles.modalHeader}>
                            <h3 className={Styles.modalTitle}>
                                Stake Details
                            </h3>
                            <button
                                onClick={() => setShowStakeDetails(null)}
                                className={Styles.modalClose}
                            >
                                <FiX />
                            </button>
                        </div>

                        {/* Coin Header */}
                        <div className={Styles.coinHeader}>
                            <div
                                className={Styles.modalCoinIcon}
                                style={showStakeDetails.iconUrl ? {
                                    backgroundImage: `url(${showStakeDetails.iconUrl})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundColor: 'transparent'
                                } : { backgroundColor: showStakeDetails.color }}
                            >
                                {!showStakeDetails.iconUrl && getDefaultIcon(showStakeDetails.symbol)}
                            </div>
                            <div className={Styles.modalCoinDetails}>
                                <div className={Styles.modalCoinName}>
                                    {showStakeDetails.coin}
                                </div>
                                <div className={Styles.modalCoinPlan}>
                                    {showStakeDetails.plan}
                                </div>
                            </div>
                        </div>

                        {/* Details List */}
                        <div className={Styles.detailsList}>
                            <div className={Styles.detailRow}>
                                <span className={Styles.detailLabel}>Staked Amount</span>
                                <span className={Styles.detailValue}>
                                    {formatNumber(showStakeDetails.amount, showStakeDetails.symbol === 'BTC' || showStakeDetails.symbol === 'ETH' ? 4 : 2)} {showStakeDetails.symbol}
                                </span>
                            </div>

                            <div className={Styles.detailRow}>
                                <span className={Styles.detailLabel}>Total Earned</span>
                                <span className={Styles.detailValue}>
                                    {formatNumber(showStakeDetails.earned, showStakeDetails.symbol === 'BTC' || showStakeDetails.symbol === 'ETH' ? 6 : 2)} {showStakeDetails.symbol}
                                </span>
                            </div>

                            <div className={Styles.detailRow}>
                                <span className={Styles.detailLabel}>APR Rate</span>
                                <span className={Styles.detailValue}>
                                    {showStakeDetails.apy}%
                                </span>
                            </div>

                            <div className={Styles.detailRow}>
                                <span className={Styles.detailLabel}>Days Remaining</span>
                                <span
                                    className={Styles.detailValue}
                                    style={{
                                        color: calculateDaysLeft(showStakeDetails).includes('days') && parseInt(calculateDaysLeft(showStakeDetails)) <= 7 ? '#f59e0b' : '#ffffff'
                                    }}
                                >
                                    {calculateDaysLeft(showStakeDetails)}
                                </span>
                            </div>

                            <div className={Styles.detailRow}>
                                <span className={Styles.detailLabel}>Start Date</span>
                                <span className={Styles.detailValue}>
                                    {showStakeDetails.rawStartDate ?
                                        new Date(showStakeDetails.rawStartDate).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        }) :
                                        'N/A'
                                    }
                                </span>
                            </div>

                            <div className={Styles.detailRow}>
                                <span className={Styles.detailLabel}>End Date</span>
                                <span className={Styles.detailValue}>
                                    {showStakeDetails.type === 'Flexible' ?
                                        'Flexible (No end date)' :
                                        showStakeDetails.rawEndDate ?
                                            new Date(showStakeDetails.rawEndDate).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            }) :
                                            showStakeDetails.rawStartDate && showStakeDetails.coinDuration ?
                                                new Date(new Date(showStakeDetails.rawStartDate).getTime() + showStakeDetails.coinDuration * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                }) :
                                                'N/A'
                                    }
                                </span>
                            </div>

                            <div className={Styles.detailRow}>
                                <span className={Styles.detailLabel}>Staking Type</span>
                                <span className={Styles.detailValue}>
                                    {showStakeDetails.type} - {showStakeDetails.duration}
                                </span>
                            </div>

                            <div className={Styles.detailRow}>
                                <span className={Styles.detailLabel}>Risk Level</span>
                                <span
                                    className={Styles.detailValue}
                                    style={{ color: getRiskColor(showStakeDetails.risk) }}
                                >
                                    {showStakeDetails.risk}
                                </span>
                            </div>

                            <div className={Styles.detailRow}>
                                <span className={Styles.detailLabel}>Reward Frequency</span>
                                <span className={Styles.detailValue}>
                                    {showStakeDetails.rewards}
                                </span>
                            </div>

                            <div className={Styles.detailRow}>
                                <span className={Styles.detailLabel}>Status</span>
                                <span className={Styles.statusBadge}>
                                    {showStakeDetails.status}
                                </span>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className={Styles.modalActions}>
                            <button
                                onClick={() => console.log('Claim rewards for stake:', showStakeDetails.id)}
                                className={Styles.claimButton}
                            >
                                Claim
                            </button>
                            <button
                                onClick={() => {
                                    setShowCancelModal(showStakeDetails);
                                    setShowStakeDetails(null);
                                    setAgreeToTerms(false);
                                }}
                                className={Styles.cancelButton}
                            >
                                Cancel Stake
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Stake Modal */}
            {showCancelModal && (
                <div className={Styles.modalOverlay}>
                    <div className={Styles.modalContent}>
                        {/* Modal Header */}
                        <div className={Styles.modalHeader}>
                            <h3 className={Styles.modalTitle}>
                                Cancel Stake
                            </h3>
                            <button
                                onClick={() => setShowCancelModal(null)}
                                className={Styles.modalClose}
                            >
                                <FiX />
                            </button>
                        </div>

                        {/* Warning */}
                        <div className={Styles.warningSection}>
                            <div className={Styles.warningTitle}>
                                Warning: Early Stake Cancellation
                            </div>
                            <div className={Styles.warningText}>
                                You are about to cancel your {showCancelModal.coin} stake before its maturity date.
                            </div>
                        </div>

                        {/* Stake Info */}
                        <div className={Styles.stakeInfoSection}>
                            <div className={Styles.stakeInfoHeader}>
                                <div
                                    className={Styles.infoCoinIcon}
                                    style={showCancelModal.iconUrl ? {
                                        backgroundImage: `url(${showCancelModal.iconUrl})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundColor: 'transparent'
                                    } : { backgroundColor: showCancelModal.color }}
                                >
                                    {!showCancelModal.iconUrl && getDefaultIcon(showCancelModal.symbol)}
                                </div>
                                <div className={Styles.infoCoinDetails}>
                                    <div className={Styles.infoCoinName}>
                                        {showCancelModal.coin} Stake
                                    </div>
                                    <div className={Styles.infoCoinPlan}>
                                        {showCancelModal.plan}
                                    </div>
                                </div>
                            </div>

                            <div className={Styles.stakeInfoGrid}>
                                <div className={Styles.infoItem}>
                                    <span className={Styles.infoLabel}>Staked Amount:</span>
                                    <div className={Styles.infoValue}>
                                        {formatNumber(showCancelModal.amount, showCancelModal.symbol === 'BTC' || showCancelModal.symbol === 'ETH' ? 4 : 2)} {showCancelModal.symbol}
                                    </div>
                                </div>
                                <div className={Styles.infoItem}>
                                    <span className={Styles.infoLabel}>Current Earnings:</span>
                                    <div className={Styles.infoValue}>
                                        {formatNumber(showCancelModal.earned, showCancelModal.symbol === 'BTC' || showCancelModal.symbol === 'ETH' ? 6 : 2)} {showCancelModal.symbol}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Terms and Conditions */}
                        <div className={Styles.termsSection}>
                            <h4 className={Styles.termsTitle}>
                                Cancellation Terms & Conditions:
                            </h4>

                            <div className={Styles.termsList}>
                                <div className={Styles.termItem}>
                                    • <strong>Early Cancellation Fee:</strong> A 2% penalty fee will be deducted from your staked amount.
                                </div>
                                <div className={Styles.termItem}>
                                    • <strong>Reward Forfeiture:</strong> All accumulated rewards will be forfeited upon cancellation.
                                </div>
                                <div className={Styles.termItem}>
                                    • <strong>Processing Time:</strong> Funds will be returned to your wallet within 24-48 hours.
                                </div>
                                <div className={Styles.termItem}>
                                    • <strong>Lock Period:</strong> You cannot restake the same asset for 7 days after cancellation.
                                </div>
                                <div className={Styles.termItem}>
                                    • <strong>Final Amount:</strong> You will receive approximately {formatNumber(showCancelModal.amount * 0.98, showCancelModal.symbol === 'BTC' || showCancelModal.symbol === 'ETH' ? 4 : 2)} {showCancelModal.symbol} after fees.
                                </div>
                            </div>

                            {/* Agreement Checkbox */}
                            <div className={Styles.agreementSection}>
                                <input
                                    type="checkbox"
                                    id="agreeTerms"
                                    checked={agreeToTerms}
                                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                                    className={Styles.agreementCheckbox}
                                />
                                <label htmlFor="agreeTerms" className={Styles.agreementLabel}>
                                    I understand and agree to the cancellation terms and conditions. I acknowledge that I will lose all accumulated rewards and pay the early cancellation fee.
                                </label>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className={Styles.modalActions}>
                            <button
                                onClick={() => setShowCancelModal(null)}
                                className={Styles.keepButton}
                            >
                                Keep Stake
                            </button>
                            <button
                                onClick={() => {
                                    if (agreeToTerms) {
                                        console.log('Confirming cancellation for stake:', showCancelModal.id);
                                        setShowCancelModal(null);
                                        setAgreeToTerms(false);
                                    }
                                }}
                                disabled={!agreeToTerms}
                                className={`${Styles.confirmCancelButton} ${!agreeToTerms ? Styles.disabled : ''}`}
                            >
                                Confirm Cancellation
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StakingPortfolioPage;
